import { msg } from '@lingui/core/macro';
import { BoardTypeEnum } from 'modules/core/domain/interfaces/supabase/overlay/auxiliary-types';
import { MatchEventType } from 'shared/types/match-event';
import { BoardPresets, boardPresets } from 'modules/template-editor/domain/consts/form/boards/boardPresets';
import { Values } from 'modules/template-editor/domain/interface/form/board';
import { getEventsOptions, getTypeOptions } from 'modules/template-editor/ui/components/elements/components/events-selector/type';
import BoardForm from 'modules/template-editor/ui/components/elements/forms/board-form';
import { useLingui } from '@lingui/react/macro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';

import { AppDispatch } from 'store/store';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import { updateBoard } from 'modules/template-editor/store/templates/actions';
import { resetPressedState } from 'modules/template-editor/store/templates/templates';
import useBoards from 'modules/template-editor/ui/hooks/use-boards';

const PRESET = BoardPresets.edit;

function useEditModal() {
  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const dispatchWithToast = useDispatchWithToast();
  const { activeBoard, boards, isProcessed, isFinished, process } = useBoards();

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback(
    // `async` + промис запроса — Formik держит кнопку формы запертой до ответа (IMGM-1561).
    async (values: Values) => {
      if (!activeBoard) return;

      if (typeof values.type !== 'object' && values?.type !== undefined && Array.isArray(values?.type)) {
        console.warn({ event: 'failed to create category: type is not object or undefined', values });
        return;
      }

      const { name, duration } = values;
      const type = (values.type as { label: string; value: BoardTypeEnum } | undefined)?.value;
      const events = (values.events as { label: string; value: MatchEventType }[] | undefined)?.map((event) => event.value);
      const isEvents = type === 'events';
      const isCarousel = type === 'carousel';

      return dispatchWithToast(
        updateBoard({
          id: activeBoard.id,
          name: name.trim(),
          type,
          duration: (isEvents || isCarousel) && duration ? +duration : undefined,
          events: isEvents ? events : undefined,
          isFavorite: isEvents ? false : activeBoard.isFavorite,
        }),
        { error: msg`Не удалось обновить табло` },
      );
    },
    [activeBoard, dispatchWithToast],
  );

  useEffect(() => {
    if (!isFinished) {
      return;
    }
    if (process !== 'update board') {
      return;
    }
    close();
    dispatch(resetPressedState());
  }, [close, dispatch, isFinished, process]);

  const initialValue = useMemo(() => {
    const events = activeBoard
      ? (getEventsOptions().filter((option) => activeBoard?.events?.includes(option.value)) as Values['events'])
      : undefined;

    return {
      name: activeBoard?.name ?? '',
      type: getTypeOptions().find((type) => type.value === activeBoard?.type),
      events: events,
      duration: activeBoard?.duration.toString(),
    } as Values;
  }, [activeBoard]);

  const existingNames = useMemo(
    () => boards.map((board) => board.name.trim()).filter((name) => name !== activeBoard?.name),
    [activeBoard?.name, boards],
  );

  return {
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type={'title-3'} className="mb-5">
          {t(boardPresets[PRESET].header)}
        </UDText>
        <BoardForm
          onSubmit={onSubmit}
          initialValues={initialValue}
          isLoading={isProcessed}
          preset={PRESET}
          existingNames={existingNames}
          disableImport
        />
      </Modal>
    ),
    close,
    open,
  };
}

export default useEditModal;
